import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Spin, Tag, notification } from "antd";
import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getLicenseStatus, activateLicense } from "../../store/license/action";
import { isForbidden } from "../../services/base.service";
import { apiErrorText } from "../../utils/apiError";
import "./LicenseStyled.scss";

// 상태별 배지 색상입니다. 백엔드가 내려주는 state 값과 1:1로 대응합니다.
// 문구는 license.state.* 에서 가져오므로 여기에는 색만 둡니다.
const STATE_COLOR = {
  active: "green",
  expiring: "orange",
  expired: "red",
  unlicensed: "default",
};

const License = () => {
  const { t } = useTranslation();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [keyInput, setKeyInput] = useState("");
  const [activating, setActivating] = useState(false);

  // 백엔드 label 은 한글 고정 문구라 그대로 쓰면 영문 화면에서 한글이 새어 나옵니다.
  // 번역 키는 type 으로 잡고, 모르는 type 일 때만 백엔드 문구로 되돌립니다.
  const typeLabel = (s) =>
    t(`license.type.${s?.type}`, { defaultValue: s?.label || "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getLicenseStatus();
      setStatus(data);
    } catch (err) {
      setStatus(null);
      notification.error({
        message: t("license.notify.loadFail"),
        description: apiErrorText(err, "license.notify.retryLater"),
        placement: "top",
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  // navigator.clipboard 는 보안 컨텍스트(HTTPS 또는 localhost)에서만 제공됩니다.
  // 평문 HTTP 로 접속하는 개발 환경에서는 객체 자체가 없으므로, 가이드 화면과
  // 동일하게 execCommand 로 대체합니다.
  const copyToClipboard = async (text) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        // 권한이 거부된 경우에도 아래 대체 경로로 이어집니다.
      }
    }

    // 여기서 예외가 새어 나가면 호출부가 경고를 띄우지 못하고 처리되지 않은
    // 거부로 남으므로, 노드 생성부터 try 안에서 처리합니다.
    let textarea = null;
    try {
      textarea = document.createElement("textarea");
      textarea.value = text;
      // 화면에 보이지 않으면서도 선택은 가능해야 하므로 화면 밖에 배치합니다.
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      textarea.setAttribute("readonly", "");
      document.body.appendChild(textarea);

      textarea.select();
      textarea.setSelectionRange(0, 99999);
      return document.execCommand("copy");
    } catch (err) {
      return false;
    } finally {
      // appendChild 이전에 실패했을 수 있으므로 부모를 확인하고 제거합니다.
      if (textarea?.parentNode) {
        textarea.parentNode.removeChild(textarea);
      }
    }
  };

  const handleCopy = async () => {
    if (!status?.serverId) return;

    if (await copyToClipboard(status.serverId)) {
      notification.success({
        message: t("license.notify.copyDone"),
        placement: "top",
      });
      return;
    }

    notification.warning({
      message: t("license.notify.copyFailTitle"),
      description: t("license.notify.copyFailDesc"),
      placement: "top",
    });
  };

  const handleActivate = async () => {
    const key = keyInput.trim();
    if (!key) return;

    setActivating(true);
    try {
      const data = await activateLicense(key);
      setStatus(data);
      setKeyInput("");
      notification.success({
        message: t("license.notify.activateDone", { label: typeLabel(data) }),
        description: t("license.notify.activateDoneDesc", {
          date: data.expiresAt,
        }),
        placement: "top",
      });
    } catch (err) {
      // 403 은 권한 부족이지 키 문제가 아닙니다. base.service 인터셉터가 이미
      // 안내하므로 여기서 "키를 다시 확인해 주세요" 를 띄우면 중복인 데다
      // 원인을 잘못 짚어 주게 됩니다.
      if (isForbidden(err)) return;
      // 백엔드는 검증 실패를 400 + code/message 로 돌려줍니다. apiErrorText 가
      // code 로 번역 문구를 고르고, 코드가 없으면 message 원문으로 되돌아갑니다.
      notification.error({
        message: t("license.notify.activateFailTitle"),
        description: apiErrorText(err, "license.notify.activateFailDesc"),
        placement: "top",
      });
    } finally {
      setActivating(false);
    }
  };

  const stateColor = STATE_COLOR[status?.state] || STATE_COLOR.unlicensed;
  const stateLabel = t(`license.state.${status?.state || "unlicensed"}`, {
    defaultValue: t("license.state.unlicensed"),
  });
  // 백엔드 message 는 한글 문장이므로 messageCode 를 우선합니다. 구버전 백엔드는
  // 코드를 내려주지 않으므로, 그때만 원문을 그대로 표시합니다.
  const statusMessage = status?.messageCode
    ? t(`license.msg.${status.messageCode}`, { defaultValue: status.message })
    : status?.message;

  return (
    <div>
      <div className="page-title p-16">{t("pageTitle.license")}</div>

      <div className="license-page">
        <section className="license-card">
          <h3>{t("license.serverIdTitle")}</h3>
          <p className="license-card__desc">{t("license.serverIdDesc")}</p>
          <div className="license-card__serverid">
            <code>
              {loading
                ? "···"
                : status?.serverId || t("license.serverIdUnavailable")}
            </code>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopy}
              disabled={!status?.serverId}
            >
              {t("license.copy")}
            </Button>
          </div>
        </section>

        <section className="license-card">
          <h3>
            {t("license.statusTitle")}
            <Tag color={stateColor}>{stateLabel}</Tag>
            {status?.type ? <Tag color="blue">{typeLabel(status)}</Tag> : null}
            <Button
              type="text"
              icon={<ReloadOutlined />}
              onClick={load}
              className="license-card__refresh"
              title={t("license.refresh")}
            />
          </h3>

          {loading ? (
            <Spin />
          ) : (
            <>
              {statusMessage ? (
                <p className="license-card__desc">{statusMessage}</p>
              ) : null}

              {status && status.state !== "unlicensed" ? (
                <dl className="license-fields">
                  <div>
                    <dt>{t("license.field.company")}</dt>
                    <dd>{status.company || "-"}</dd>
                  </div>
                  <div>
                    <dt>{t("license.field.lid")}</dt>
                    <dd>{status.lid || "-"}</dd>
                  </div>
                  <div>
                    <dt>{t("license.field.issuedAt")}</dt>
                    <dd>{status.issuedAt || "-"}</dd>
                  </div>
                  <div>
                    <dt>{t("license.field.expiresAt")}</dt>
                    <dd>{status.expiresAt || "-"}</dd>
                  </div>
                  <div>
                    <dt>{t("license.field.daysLeft")}</dt>
                    <dd>
                      {status.daysLeft >= 0
                        ? t("license.daysLeft", { days: status.daysLeft })
                        : t("license.daysOverdue", { days: -status.daysLeft })}
                    </dd>
                  </div>
                  <div>
                    <dt>{t("license.field.nodes")}</dt>
                    <dd>
                      {status.nodes > 0
                        ? t("license.nodeLimit", { nodes: status.nodes })
                        : t("license.nodeUnlimited")}
                    </dd>
                  </div>
                  {status.activatedAt ? (
                    <div>
                      <dt>{t("license.field.activatedAt")}</dt>
                      <dd>
                        {status.activatedAt}
                        {status.activatedBy ? ` (${status.activatedBy})` : ""}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              ) : null}

              {status?.readOnly ? (
                <div className="license-readonly">{t("license.readOnly")}</div>
              ) : null}
            </>
          )}
        </section>

        <section className="license-card">
          <h3>{t("license.activateTitle")}</h3>
          <Input.TextArea
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            rows={4}
            spellCheck={false}
            placeholder="GRPLIC1.trial.eyJsaWQiOi..."
            className="license-key-input"
          />
          <Button
            type="primary"
            onClick={handleActivate}
            loading={activating}
            disabled={!keyInput.trim()}
            className="license-activate-btn"
          >
            {t("license.activate")}
          </Button>
        </section>
      </div>
    </div>
  );
};

export default License;
